<template>
  <div class="bugreports-page">
    <header class="page-head">
      <h1>Bug Reports Review <small class="admin-note">(Admin / Moderator)</small></h1>
      <p class="sub">Review, triage, and resolve user-submitted legacy and V2 analysis reports.</p>
    </header>

    <p v-if="notificationMessage" :class="['toast', notificationType]">
      {{ notificationMessage }}
      <button class="toast__dismiss" @click="clearNotification" aria-label="Dismiss">×</button>
    </p>

    <section class="card -tight">
      <div class="filters">
        <div class="filter-row">
          <div class="filter-chip">
            <span>Status:</span>
            <button
              class="chip"
              :class="{ active: statusFilter === 'all' }"
              @click="setStatusFilter('all')"
            >All</button>
            <button
              class="chip"
              :class="{ active: statusFilter === 'reported' }"
              @click="setStatusFilter('reported')"
            >Open</button>
            <button
              class="chip"
              :class="{ active: statusFilter === 'resolved' }"
              @click="setStatusFilter('resolved')"
            >Resolved</button>
          </div>
        </div>
      </div>
    </section>

    <section class="card">
      <header class="card__head">
        <h2>Reports</h2>
        <span class="muted" v-if="totalPages">Page {{ currentPage }} of {{ totalPages }}</span>
      </header>

      <div v-if="isLoading" class="skeleton">
        <div class="skeleton-row" v-for="n in 6" :key="n"></div>
      </div>

      <div v-else>
        <div v-if="filteredReports.length" class="table-wrap">
          <table class="nice-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Request</th>
                <th>User</th>
                <th>Transaction Hash</th>
                <th>Description</th>
                <th>Reported</th>
                <th>Status</th>
                <th style="width:150px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="report in filteredReports" :key="report.bug_key || report.bug_id">
                <td class="mono">#{{ report.bug_id }}</td>
                <td>
                  <div class="request-cell">
                    <span :class="['system-pill', isV2Report(report) ? 'system-pill--v2' : 'system-pill--legacy']">
                      {{ isV2Report(report) ? 'V2' : 'Legacy' }}
                    </span>
                    <span class="mono">{{ requestDisplayId(report) }}</span>
                    <button
                      class="icon-btn"
                      title="Copy request identifier"
                      @click="copy(requestIdentifier(report))"
                    >📋</button>
                  </div>
                  <div class="tiny muted" v-if="report.request_status">
                    Request: {{ report.request_status }}
                  </div>
                </td>
                <td class="semi">{{ report.username || '—' }}</td>
                <td class="mono truncate">
                  <span :title="report.transaction_hash">{{ report.transaction_hash || '—' }}</span>
                  <button
                    v-if="report.transaction_hash"
                    class="icon-btn"
                    title="Copy transaction hash"
                    @click="copy(report.transaction_hash)"
                  >📋</button>
                </td>
                <td class="desc">
                  <details>
                    <summary class="truncate" :title="report.description">
                      {{ report.description || '—' }}
                    </summary>
                    <pre class="desc-full">{{ report.description || '—' }}</pre>
                  </details>
                </td>
                <td class="semi">{{ formatTime(report.report_time) }}</td>
                <td>
                  <span
                    class="pill"
                    :class="normalizedStatus(report) === 'resolved' ? 'pill--ok' : 'pill--queued'"
                  >
                    {{ statusLabel(report) }}
                  </span>
                  <div v-if="report.resolved_by" class="tiny muted resolved-by">
                    by {{ report.resolved_by }}
                  </div>
                </td>
                <td>
                  <div class="row-actions">
                    <button
                      v-if="normalizedStatus(report) !== 'resolved'"
                      class="btn btn--ghost small"
                      @click="openResolve(report)"
                    >
                      Mark Resolved
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else class="empty">No reports found.</p>

        <div class="cards" v-if="filteredReports.length">
          <article
            v-for="report in filteredReports"
            :key="'m-' + (report.bug_key || report.bug_id)"
            class="mcard"
          >
            <header class="mcard__head">
              <div class="mcard__title">
                <span class="mono">#{{ report.bug_id }}</span>
                <span :class="['system-pill', isV2Report(report) ? 'system-pill--v2' : 'system-pill--legacy']">
                  {{ isV2Report(report) ? 'V2' : 'Legacy' }}
                </span>
              </div>
              <span
                class="pill"
                :class="normalizedStatus(report) === 'resolved' ? 'pill--ok' : 'pill--queued'"
              >
                {{ statusLabel(report) }}
              </span>
            </header>
            <dl class="kv">
              <div>
                <dt>Request</dt>
                <dd class="mono break">{{ requestDisplayId(report) }}</dd>
              </div>
              <div>
                <dt>User</dt>
                <dd>{{ report.username || '—' }}</dd>
              </div>
              <div>
                <dt>Tx Hash</dt>
                <dd class="mono break">
                  {{ report.transaction_hash || '—' }}
                  <button
                    v-if="report.transaction_hash"
                    class="icon-btn"
                    @click="copy(report.transaction_hash)"
                  >📋</button>
                </dd>
              </div>
              <div>
                <dt>Reported</dt>
                <dd>{{ formatTime(report.report_time) }}</dd>
              </div>
              <div>
                <dt>Description</dt>
                <dd class="prewrap">{{ report.description || '—' }}</dd>
              </div>
            </dl>
            <div class="mcard__actions">
              <button
                v-if="normalizedStatus(report) !== 'resolved'"
                class="btn btn--ghost small"
                @click="openResolve(report)"
              >Mark Resolved</button>
            </div>
          </article>
        </div>

        <div class="pagination">
          <button class="btn btn--ghost" @click="previousPage" :disabled="currentPage <= 1">Previous</button>
          <span>Page {{ currentPage }} of {{ totalPages || 1 }}</span>
          <button class="btn btn--ghost" @click="nextPage" :disabled="currentPage >= totalPages">Next</button>
        </div>
      </div>
    </section>

    <div v-if="resolveTarget" class="modal" role="dialog" aria-modal="true" aria-labelledby="resolveTitle">
      <div class="modal__panel">
        <h3 id="resolveTitle">Resolve bug report?</h3>
        <p>
          Mark report <strong>#{{ resolveTarget.bug_id }}</strong> for
          <strong>{{ isV2Report(resolveTarget) ? 'V2' : 'legacy' }} request {{ requestDisplayId(resolveTarget) }}</strong>
          as resolved?
        </p>
        <p class="tiny muted">
          The user will see the resolved status in their request history.
        </p>
        <div class="modal__actions">
          <button class="btn btn--danger" @click="confirmResolve" :disabled="isResolving">
            {{ isResolving ? 'Resolving…' : 'Yes, resolve' }}
          </button>
          <button class="btn btn--ghost" @click="resolveTarget = null" :disabled="isResolving">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AuthService from '@/services/AuthService';

export default {
  name: 'BugReports',
  data() {
    return {
      reports: [],
      currentPage: 1,
      totalPages: 0,
      notificationMessage: '',
      notificationType: '',
      isLoading: false,
      isResolving: false,
      statusFilter: 'all',
      resolveTarget: null,
    };
  },
  computed: {
    filteredReports() {
      if (this.statusFilter === 'all') return this.reports;
      return this.reports.filter(
        (report) => this.normalizedStatus(report) === this.statusFilter
      );
    },
  },
  methods: {
    normalizedStatus(report) {
      const status = String(report?.status || 'reported').toLowerCase();
      return status === 'resolved' ? 'resolved' : 'reported';
    },
    statusLabel(report) {
      return this.normalizedStatus(report) === 'resolved' ? 'Resolved' : 'Open';
    },
    isV2Report(report) {
      return String(report?.request_system || '').toLowerCase() === 'casebuddy_lite';
    },
    requestDisplayId(report) {
      if (report?.display_id) return String(report.display_id);
      if (this.isV2Report(report) && report?.request_uuid) {
        return String(report.request_uuid).slice(0, 8);
      }
      return report?.request_id ? String(report.request_id) : 'Unknown';
    },
    requestIdentifier(report) {
      return this.isV2Report(report)
        ? String(report?.request_uuid || '')
        : String(report?.request_id || '');
    },
    formatTime(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    },
    copy(text) {
      const value = String(text || '').trim();
      if (!value) return;
      navigator.clipboard?.writeText(value).catch(() => {});
    },
    setStatusFilter(value) {
      this.statusFilter = value;
    },
    showNotification(message, type) {
      this.notificationMessage = message;
      this.notificationType = type;
    },
    clearNotification() {
      this.notificationMessage = '';
      this.notificationType = '';
    },
    fetchReports() {
      this.isLoading = true;
      axios.get('etherscan/fetch_bug_reports.php', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: {
          page: this.currentPage,
          limit: 10,
        },
      })
      .then((response) => {
        if (response.data.status === 'success') {
          this.reports = response.data.reports || [];
          this.totalPages = Number(response.data.total_pages || 0) || 1;
          this.clearNotification();
        } else {
          this.showNotification(
            'Failed to fetch reports: ' + response.data.message,
            'error'
          );
        }
      })
      .catch((error) => {
        this.showNotification(
          'Error fetching reports: ' + error.message,
          'error'
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
        this.fetchReports();
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
        this.fetchReports();
      }
    },
    openResolve(report) {
      this.resolveTarget = report;
    },
    async confirmResolve() {
      if (!this.resolveTarget || this.isResolving) return;

      this.isResolving = true;
      try {
        const response = await axios.post(
          'etherscan/resolve_bug.php',
          {
            bug_key: this.resolveTarget.bug_key,
            bug_id: this.resolveTarget.bug_id,
            request_system: this.resolveTarget.request_system,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        if (response.data.status !== 'success') {
          throw new Error(response.data.message || 'Could not resolve bug report.');
        }

        this.showNotification('Bug resolved successfully!', 'success');
        this.resolveTarget = null;
        this.fetchReports();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Could not resolve bug report.';
        this.showNotification('Error resolving bug: ' + message, 'error');
      } finally {
        this.isResolving = false;
      }
    },
  },
  mounted() {
    AuthService.getUserRole()
      .then(({ role }) => {
        if (!['admin', 'moderator'].includes(role)) {
          this.$router.replace('/access-denied');
          return;
        }
        this.fetchReports();
      })
      .catch(() => {
        this.$router.replace('/login');
      });
  },
};
</script>

<style scoped>
.bugreports-page { max-width:1280px; margin:0 auto; padding:1rem; color:#1f2a37; }
.page-head h1 { margin:0; font-size:1.5rem; font-weight:800; color:#0f2740; }
.sub { color:#667085; margin:.15rem 0 .8rem; }
.admin-note { font-size:.8rem; color:#94a3b8; }
.toast { position:relative; padding:.6rem .8rem; border-radius:10px; margin:.2rem 0 1rem; font-weight:600; border:1px solid transparent; }
.toast.success { background:#ecfdf5; color:#065f46; border-color:#a7f3d0; }
.toast.error { background:#fef2f2; color:#991b1b; border-color:#fecaca; }
.toast__dismiss { position:absolute; top:6px; right:8px; border:none; background:transparent; font-size:1.1rem; cursor:pointer; }
.card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 10px 20px rgba(16,24,40,.06); padding:1rem; margin-bottom:1rem; }
.card.-tight { padding:.8rem .9rem; }
.card__head { display:flex; justify-content:space-between; align-items:center; margin-bottom:.6rem; }
.muted { color:#6b7280; }
.tiny { font-size:.78rem; }
.filters { display:flex; flex-wrap:wrap; gap:.6rem; }
.filter-row { display:flex; gap:.6rem; align-items:center; }
.filter-chip span { color:#475467; margin-right:.35rem; }
.chip { border:1px solid #e5e7eb; background:#f9fafb; padding:.35rem .6rem; border-radius:10px; cursor:pointer; font-weight:600; }
.chip.active { background:#eef2ff; border-color:#c7d2fe; color:#4338ca; }
.skeleton { display:grid; gap:.5rem; }
.skeleton-row { height:42px; background:linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 37%,#f3f4f6 63%); background-size:400% 100%; animation:shimmer 1.2s infinite; border-radius:10px; }
@keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:0 0} }
.table-wrap { overflow-x:auto; border:1px solid #e5e7eb; border-radius:12px; }
.nice-table { width:100%; border-collapse:separate; border-spacing:0; min-width:1160px; table-layout:fixed; }
.nice-table thead th { position:sticky; top:0; z-index:2; background:#f3f6fb; color:#0f2740; text-align:left; font-weight:700; font-size:.92rem; border-bottom:1px solid #e5e7eb; padding:.7rem; }
.nice-table td { padding:.6rem .7rem; border-bottom:1px solid #eef1f5; vertical-align:top; }
.request-cell { display:flex; align-items:center; gap:.35rem; flex-wrap:wrap; }
.system-pill { display:inline-flex; align-items:center; padding:.12rem .42rem; border-radius:999px; font-size:.7rem; font-weight:800; border:1px solid transparent; }
.system-pill--legacy { background:#f3f4f6; color:#374151; border-color:#d1d5db; }
.system-pill--v2 { background:#e8f1ff; color:#1d4ed8; border-color:#bfdbfe; }
.desc details summary { cursor:pointer; list-style:none; }
.desc details summary::-webkit-details-marker { display:none; }
.desc-full { white-space:pre-wrap; margin:.4rem 0 0; max-height:260px; overflow:auto; }
.truncate { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.mono { font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono",monospace; }
.semi { font-weight:600; }
.icon-btn { border:1px solid #e5e7eb; background:#fff; border-radius:8px; padding:.1rem .35rem; margin-left:.15rem; cursor:pointer; }
.icon-btn:hover { background:#f3f4f6; }
.pill { display:inline-block; padding:.15rem .5rem; border-radius:999px; font-size:.78rem; font-weight:700; border:1px solid transparent; }
.pill--ok { background:#e8faf3; color:#065f46; border-color:#a7f3d0; }
.pill--queued { background:#eef2ff; color:#4338ca; border-color:#c7d2fe; }
.resolved-by { margin-top:.2rem; }
.btn { padding:.55rem .8rem; border-radius:10px; border:1px solid rgba(15,38,61,.15); background:linear-gradient(180deg,#fff,#f3f4f6); cursor:pointer; font-weight:600; }
.btn:hover { background:linear-gradient(180deg,#f9fafb,#eef2f7); }
.btn:disabled { cursor:not-allowed; opacity:.6; }
.btn--ghost { background:#f9fafb; border:1px solid #e5e7eb; }
.btn--danger { background:#fee2e2; border:1px solid #fecaca; color:#991b1b; }
.small { padding:.4rem .6rem; font-size:.9rem; }
.row-actions { display:flex; gap:.4rem; flex-wrap:wrap; }
.empty { color:#6b7280; padding:.5rem 0; text-align:center; }
.pagination { display:flex; gap:.6rem; align-items:center; justify-content:center; margin-top:.8rem; }
.cards { display:none; }
@media (max-width:760px) {
  .table-wrap { display:none; }
  .cards { display:grid; gap:.8rem; }
  .mcard { border:1px solid #e5e7eb; border-radius:12px; background:#fff; box-shadow:0 6px 14px rgba(16,24,40,.06); padding:.9rem; }
  .mcard__head { display:flex; align-items:center; justify-content:space-between; gap:.6rem; margin-bottom:.35rem; }
  .mcard__title { display:flex; gap:.4rem; align-items:center; font-weight:700; }
  .kv { display:grid; gap:.35rem; }
  .kv > div { display:grid; grid-template-columns:90px 1fr; gap:.4rem; align-items:baseline; }
  .kv dt { color:#6b7280; font-size:.86rem; }
  .kv dd { margin:0; }
  .prewrap { white-space:pre-wrap; }
  .break { word-break:break-all; }
  .mcard__actions { margin-top:.6rem; display:flex; flex-wrap:wrap; gap:.4rem; }
}
.modal { position:fixed; inset:0; background:rgba(0,0,0,.45); display:grid; place-items:center; z-index:1000; }
.modal__panel { background:#fff; border-radius:14px; border:1px solid #e5e7eb; box-shadow:0 20px 50px rgba(0,0,0,.25); padding:1rem; width:min(520px,92vw); }
.modal__panel h3 { margin:0 0 .35rem; }
.modal__actions { display:flex; gap:.5rem; justify-content:flex-end; margin-top:.8rem; }
</style>
